/**
 * Reconciliation Card Component
 *
 * Feature: 023-service-principal-registry
 * Task: T033 - Create ReconciliationCard component for status display
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Card displaying reconciliation status with run trigger button
 */

import React from 'react';
import { SpReconciliationLog, ReconciliationType, ReconciliationStatus } from '../types';
import { formatEastern } from '@/utils/dateFormatter';

interface ReconciliationCardProps {
  isRunning: boolean;
  lastRun: SpReconciliationLog | null;
  nextScheduledRun: string | null;
  onTriggerRun: (type: ReconciliationType) => void;
  isTriggering?: boolean;
}

/**
 * Status badge colors
 */
const statusColors: Record<
  ReconciliationStatus,
  {
    bg: string;
    text: string;
    icon: string;
  }
> = {
  RUNNING: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-800 dark:text-blue-300',
    icon: '🔄',
  },
  COMPLETED: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-800 dark:text-green-300',
    icon: '✅',
  },
  PARTIAL: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-800 dark:text-yellow-300',
    icon: '⚠️',
  },
  FAILED: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-800 dark:text-red-300',
    icon: '❌',
  },
};

/**
 * Reconciliation type labels
 */
const typeLabels: Record<ReconciliationType, string> = {
  daily_permissions: 'Daily Permissions Check',
  weekly_entra: 'Weekly Entra Verification',
  weekly_export: 'Weekly Export',
  manual: 'Manual Run',
};

/**
 * Format date for display
 */
function formatDateTime(date: Date | string | null): string {
  if (!date) return 'Never';
  const d = new Date(date);
  return formatEastern(d);
}

/**
 * Format duration
 */
function formatDuration(start: Date | string, end: Date | string | null): string {
  if (!end) return 'In progress...';
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const durationMs = endMs - startMs;

  if (durationMs < 1000) return `${durationMs}ms`;
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  return `${Math.round(durationMs / 60000)}m ${Math.round((durationMs % 60000) / 1000)}s`;
}

/**
 * Format relative time until next run
 */
function formatRelativeTime(date: string | null): string {
  if (!date) return 'Not scheduled';
  const now = new Date();
  const target = new Date(date);
  const diffMs = target.getTime() - now.getTime();

  if (diffMs < 0) return 'Overdue';
  if (diffMs < 3600000) return `in ${Math.round(diffMs / 60000)} minutes`;
  if (diffMs < 86400000) return `in ${Math.round(diffMs / 3600000)} hours`;
  return `in ${Math.round(diffMs / 86400000)} days`;
}

/**
 * Reconciliation Card Component
 */
export const ReconciliationCard: React.FC<ReconciliationCardProps> = ({
  isRunning,
  lastRun,
  nextScheduledRun,
  onTriggerRun,
  isTriggering = false,
}) => {
  const lastStatus = lastRun ? statusColors[lastRun.status] : null;

  return (
    <div className='bg-[var(--color-bg-card)] rounded-lg shadow border border-[var(--color-border)] p-6'>
      <div className='flex items-center justify-between mb-4'>
        <h3 className='text-lg font-semibold text-[var(--color-text-primary)]'>Reconciliation Status</h3>
        {isRunning && (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 animate-pulse'>
            <span className='mr-1'>🔄</span>
            Running...
          </span>
        )}
      </div>

      {/* Current Status */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
        {/* Last Run Status */}
        <div className='p-4 bg-[var(--color-bg-subtle)] rounded-lg'>
          <p className='text-xs text-[var(--color-text-secondary)] uppercase tracking-wide mb-1'>Last Run</p>
          {lastRun ? (
            <div>
              <div className='flex items-center mb-1'>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${lastStatus?.bg} ${lastStatus?.text}`}
                >
                  <span className='mr-1'>{lastStatus?.icon}</span>
                  {lastRun.status}
                </span>
              </div>
              <p className='text-sm text-[var(--color-text-secondary)]'>{typeLabels[lastRun.reconciliationType]}</p>
              <p className='text-xs text-[var(--color-text-secondary)]'>{formatDateTime(lastRun.startedAt)}</p>
            </div>
          ) : (
            <p className='text-sm text-[var(--color-text-secondary)]'>No runs yet</p>
          )}
        </div>

        {/* Statistics */}
        {lastRun && (
          <div className='p-4 bg-[var(--color-bg-subtle)] rounded-lg'>
            <p className='text-xs text-[var(--color-text-secondary)] uppercase tracking-wide mb-1'>Last Run Stats</p>
            <div className='space-y-1'>
              <p className='text-sm'>
                <span className='text-[var(--color-text-secondary)]'>SPs Checked:</span>{' '}
                <span className='font-medium'>{lastRun.spsChecked}</span>
              </p>
              <p className='text-sm'>
                <span className='text-[var(--color-text-secondary)]'>Assignments:</span>{' '}
                <span className='font-medium'>{lastRun.assignmentsChecked}</span>
              </p>
              <p className='text-sm'>
                <span className='text-[var(--color-text-secondary)]'>Drifts Found:</span>{' '}
                <span
                  className={`font-medium ${lastRun.driftsDetected > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}
                >
                  {lastRun.driftsDetected}
                </span>
              </p>
              <p className='text-sm'>
                <span className='text-[var(--color-text-secondary)]'>Auto-Fixed:</span>{' '}
                <span className='font-medium text-green-600 dark:text-green-400'>{lastRun.driftsFixed}</span>
              </p>
              <p className='text-sm'>
                <span className='text-[var(--color-text-secondary)]'>Manual Review:</span>{' '}
                <span
                  className={`font-medium ${lastRun.driftsManual > 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-[var(--color-text-secondary)]'}`}
                >
                  {lastRun.driftsManual}
                </span>
              </p>
            </div>
          </div>
        )}

        {/* Next Scheduled */}
        <div className='p-4 bg-[var(--color-bg-subtle)] rounded-lg'>
          <p className='text-xs text-[var(--color-text-secondary)] uppercase tracking-wide mb-1'>Next Scheduled</p>
          <p className='text-sm font-medium text-[var(--color-text-primary)]'>
            {formatRelativeTime(nextScheduledRun)}
          </p>
          {nextScheduledRun && (
            <p className='text-xs text-[var(--color-text-secondary)]'>{formatDateTime(nextScheduledRun)}</p>
          )}
        </div>
      </div>

      {/* Duration */}
      {lastRun && lastRun.completedAt && (
        <div className='mb-4 p-3 bg-[var(--color-bg-subtle)] rounded'>
          <p className='text-sm text-[var(--color-text-secondary)]'>
            <span className='font-medium'>Duration:</span>{' '}
            {formatDuration(lastRun.startedAt, lastRun.completedAt)}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className='flex flex-wrap gap-2'>
        <button
          onClick={() => onTriggerRun('daily_permissions')}
          disabled={isRunning || isTriggering}
          className='inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {isTriggering ? (
            <>
              <span className='animate-spin mr-2'>⏳</span>
              Starting...
            </>
          ) : (
            <>
              <span className='mr-2'>▶️</span>
              Run Permissions Check
            </>
          )}
        </button>
        <button
          onClick={() => onTriggerRun('weekly_entra')}
          disabled={isRunning || isTriggering}
          className='inline-flex items-center px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-subtle)] rounded-md hover:bg-[var(--color-bg-subtle)] disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <span className='mr-2'>🔍</span>
          Verify Entra IDs
        </button>
        <button
          onClick={() => onTriggerRun('weekly_export')}
          disabled={isRunning || isTriggering}
          className='inline-flex items-center px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-subtle)] rounded-md hover:bg-[var(--color-bg-subtle)] disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <span className='mr-2'>📤</span>
          Export Now
        </button>
      </div>
    </div>
  );
};

export default ReconciliationCard;
